import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { fetchChangelog } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Sparkles, Wrench, Moon, Star, Smartphone, BookOpen, Rocket, Tag } from "lucide-react";

export const DEFAULT_CHANGELOG_CATEGORIES = [
  { key: "feature", en: "Features", hi: "विशेषताएँ" },
  { key: "fix", en: "Bug Fixes", hi: "सुधार" },
  { key: "panchanga_premium", en: "Panchangam Premium", hi: "पंचांग प्रीमियम" },
  { key: "dharmartha_premium", en: "Dharmartha Premium", hi: "धर्मार्थ प्रीमियम" },
  { key: "mobile", en: "Mobile Apps", hi: "मोबाइल ऐप" },
  { key: "content", en: "Content", hi: "सामग्री" },
  { key: "release", en: "New Release", hi: "नया संस्करण" },
];

// Visual treatment per category key. Unknown keys fall back to a neutral style.
const STYLE = {
  feature: { icon: Sparkles, cls: "bg-saffron/12 text-saffron border-saffron/30" },
  fix: { icon: Wrench, cls: "bg-emerald-500/12 text-emerald-600 border-emerald-500/30" },
  panchanga_premium: { icon: Moon, cls: "bg-[var(--gold)]/15 text-gold border-[var(--gold)]/40" },
  dharmartha_premium: { icon: Star, cls: "bg-violet-500/12 text-violet-600 border-violet-500/30" },
  mobile: { icon: Smartphone, cls: "bg-sky-500/12 text-sky-600 border-sky-500/30" },
  content: { icon: BookOpen, cls: "bg-rose-500/12 text-rose-600 border-rose-500/30" },
  release: { icon: Rocket, cls: "bg-ink/10 text-ink-2 border-line" },
};
// Legacy tags from before the 7-category system → fold into sensible buckets.
const LEGACY = { update: "feature", announcement: "release", new: "release" };

const styleFor = (k) => STYLE[k] || { icon: Tag, cls: "bg-ink/10 text-ink-2 border-line" };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };

export default function Changelog() {
  const { pick } = useLang();
  const settings = useSettings();
  const cats = (Array.isArray(settings?.changelog_categories) && settings.changelog_categories.length
    ? settings.changelog_categories : DEFAULT_CHANGELOG_CATEGORIES).filter((c) => c && c.key);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { fetchChangelog().then(setItems).catch(() => setItems([])); }, []);

  const normTag = (t) => { const k = t || "feature"; return cats.find((c) => c.key === k) ? k : (LEGACY[k] || k); };

  // Group entries by category, honouring the admin category order.
  const groups = useMemo(() => {
    if (!items) return [];
    const byKey = {};
    items.forEach((e) => { const k = normTag(e.tag); (byKey[k] = byKey[k] || []).push(e); });
    const ordered = cats.map((c) => ({ ...c, entries: byKey[c.key] || [] }));
    // Any entries whose tag isn't a known category → trailing "Other" group.
    const known = new Set(cats.map((c) => c.key));
    const otherKeys = Object.keys(byKey).filter((k) => !known.has(k));
    if (otherKeys.length) {
      const other = otherKeys.flatMap((k) => byKey[k]);
      ordered.push({ key: "__other", en: "Other", hi: "अन्य", entries: other });
    }
    return ordered.filter((g) => g.entries.length);
  }, [items, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = filter === "all" ? groups : groups.filter((g) => g.key === filter);
  const countFor = (key) => (groups.find((g) => g.key === key)?.entries.length) || 0;

  return (
    <>
      <Seo path="/changelog" title="What's New · Dharmartha" description="New features, improvements, premium tools and content across Dharmartha." />
      <PageHero
        overline={pick("नवीनतम", "What's New")}
        deva={pick("नया क्या है", "What's New")}
        tagline={pick("धर्मार्थ में नई सुविधाएँ, सुधार, प्रीमियम उपकरण एवं सामग्री।", "New features, fixes, premium tools and content across Dharmartha.")}
      />
      <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16" data-testid="changelog-page">
        {items === null ? (
          <p className="py-20 text-center text-ink-3">…</p>
        ) : items.length === 0 ? (
          <p className="py-20 text-center text-ink-3">{pick("अभी कोई प्रविष्टि नहीं।", "No entries yet.")}</p>
        ) : (
          <>
            {/* Category filter chips */}
            <div className="mb-10 flex flex-wrap gap-2" data-testid="changelog-filters">
              <button data-testid="changelog-filter-all" onClick={() => setFilter("all")}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === "all" ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
                {pick("सभी", "All")}
              </button>
              {groups.filter((g) => g.key !== "__other").map((g) => {
                const S = styleFor(g.key);
                return (
                  <button key={g.key} data-testid={`changelog-filter-${g.key}`} onClick={() => setFilter(g.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === g.key ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
                    <S.icon className="h-3.5 w-3.5" /> {pick(g.hi, g.en)}
                    <span className="opacity-70">· {countFor(g.key)}</span>
                  </button>
                );
              })}
            </div>

            {/* Grouped headed sections, What's New card style */}
            <div className="space-y-14">
              {shown.map((g) => {
                const S = styleFor(g.key);
                return (
                  <div key={g.key} data-testid={`changelog-section-${g.key}`}>
                    <div className="mb-6 flex items-center gap-3 border-b border-line pb-3">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${S.cls}`}><S.icon className="h-5 w-5" /></span>
                      <h2 className="font-serif-latin text-2xl text-ink">{pick(g.hi, g.en)}</h2>
                      <span className="text-sm text-ink-3">· {g.entries.length}</span>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {g.entries.map((e, i) => (
                        <motion.article key={e.id} data-testid={`changelog-${e.id}`}
                          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                          transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                          className="flex h-full flex-col border border-line bg-surface p-5 transition-all hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${S.cls}`}>
                              <S.icon className="h-3 w-3" /> {pick(g.hi, g.en)}
                            </span>
                            <span className="text-xs text-ink-3">{fmtDate(e.date || e.created_at)}</span>
                          </div>
                          <h3 className="mt-3 font-serif-latin text-lg text-ink">{e.title}</h3>
                          {e.body && <p className="mt-1.5 flex-1 whitespace-pre-line text-sm leading-relaxed text-ink-2">{e.body}</p>}
                        </motion.article>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </>
  );
}
